import ConnectGoogleAccComponent from "@components/ManageAppointmentPage/ConnectGoogleAccComponent/ConnectGoogleAccComponent"
import NavigationsPage from "@containers/NavigationsPage/NavigationPage"
import PageWrapper from "@components/Common/PageWrapper"
import ArrowSquareLeftIcon from "@assets/icons/arrowSquareLeft"
import AppointmentDetailsTable from "@components/ManageAppointmentPage/AppointmentDetailsTable"
import PlusIcon from "@assets/icons/plus"
import FilterIcon from "@assets/icons/filter"
import styles from "./ManageAppointmentPage.module.css"
import TimeAppointments from "@components/ManageAppointmentPage/TimeAppointments"
import AppointmentCalendar from "./AppointmentCalendar"
import { useEffect, useState } from "react"
import useBooking from "@hooks/useBooking"
import { useSelector } from "react-redux"
import FullpageLoadingState from "@components/Common/FullpageLoadingState"
import { Link, useLocation } from "react-router-dom"
import GoBack from "@components/Common/GoBack"
import FilterContainer from "@components/Common/Filters/FilterContainer"

function ManageAppointmentPage() {
  const [showAppointmentInfo, setShowAppointmentInfo] = useState(true)
  const [showFilters, setShowFilters] = useState(false)
  const [filterToBeExpanded, setFilterToBeExpanded] = useState(null)
  const [paginationState, setPaginationState] = useState({
    page: 1,
    limit: 10,
  })
  const [bookingData, setBookingData] = useState([])
  const location = useLocation()

  const {
    handleIsAccessTokenValid,
    handleGetCalendarBooking,
    handleGetBooking,
  } = useBooking()

  const {
    isAccessTokenValidLoading,
    accessTokenValid,
    isGetCalendarBookingLoading,
    isGetBookingLoading,
  } = useSelector((state) => state.booking)

  useEffect(() => {
    const currentDate = new Date()
    handleGetCalendarBooking({ date: currentDate.toISOString() })
  }, [handleGetCalendarBooking])

  useEffect(() => {
    const fetchBookings = async () => {
      const result = await handleGetBooking(paginationState)
      // console.log("Pagination State:", paginationState)
      if (result && result.data) {
        setBookingData(result.data)
      }
    }
    fetchBookings()
  }, [handleGetBooking, paginationState])

  // Additional useEffect to ensure filter data is updated when bookingData changes
  useEffect(() => {
    // Force re-render when data changes to update filter options
    setFilterToBeExpanded(null)
  }, [bookingData])

  const handleCalendarDateChange = (value) => {
    handleGetCalendarBooking({ date: value.toISOString() })
  }
  const [signedIN, setSignedIn] = useState(false)
  const [authCheckComplete, setAuthCheckComplete] = useState(false)

  useEffect(() => {
    let isMounted = true

    const checkAuth = async () => {
      if (accessTokenValid) {
        setSignedIn(true)
        setAuthCheckComplete(true)
      } else {
        try {
          const result = await handleIsAccessTokenValid()
          if (isMounted) {
            setSignedIn(result === true)
            setAuthCheckComplete(true)
          }
        } catch (error) {
          if (isMounted) {
            setSignedIn(false)
            setAuthCheckComplete(true)
          }
        }
      }
    }

    checkAuth()
    return () => {
      isMounted = false
    }
  }, [accessTokenValid, handleIsAccessTokenValid])

  // Add effect to check for refresh flag in navigation state
  useEffect(() => {
    if (location.state?.refreshData) {
      // Clear the state flag to prevent endless refresh cycles
      window.history.replaceState({}, document.title)

      // Refresh appointment data
      const fetchBookings = async () => {
        const result = await handleGetBooking(paginationState)
        if (result && result.data) {
          setBookingData(result.data)
        }
      }
      fetchBookings()

      // Also refresh calendar data
      const currentDate = new Date()
      currentDate.setHours(0, 0, 0, 0)
      handleGetCalendarBooking({ date: currentDate.toISOString() })
    }
  }, [
    location.state,
    handleGetBooking,
    handleGetCalendarBooking,
    paginationState,
  ])

  // Filter configurations
  const filters = [
    {
      filterName: "First Name",
      filterProperty: "firstName",
      filterPath: "firstName", // Changed from patient.firstName
      dataSource: bookingData,
    },
    {
      filterName: "Last Name",
      filterProperty: "lastName",
      filterPath: "lastName", // Changed from patient.lastName
      dataSource: bookingData,
    },
    {
      filterName: "Contact",
      filterProperty: "contact",
      filterPath: "contact", // Changed from patient.contactNumber
      dataSource: bookingData,
    },
    {
      filterName: "Location",
      filterProperty: "location",
      filterPath: "location",
      dataSource: bookingData,
    },
    {
      filterName: "Type",
      filterProperty: "type",
      filterPath: "type", // Changed from appointmentType
      dataSource: bookingData,
    },
    {
      filterName: "Duration",
      filterProperty: "duration",
      filterPath: "duration",
      dataSource: bookingData,
    },
    {
      filterName: "Status",
      filterProperty: "status",
      filterPath: "status",
      dataSource: bookingData,
    },
    {
      filterName: "Date",
      filterProperty: "date",
      filterPath: "startDateTime", // Changed from appointmentDate
      dataSource: bookingData,
    },
  ]

  const valueTransformFunctions = {
    date: (value) => {
      if (!value) return ""
      const date = new Date(value)
      return date.toLocaleDateString()
    },
    duration: (value) => {
      // Convert duration to number
      const numValue = parseInt(value, 10)
      return isNaN(numValue) ? value : numValue
    },
  }

  return (
    <NavigationsPage>
      <PageWrapper className={styles.manageAppointmentPage}>
        <div className={styles.header}>
          <div>
            <GoBack className={styles.manageAppointmentBackButton}>
              <ArrowSquareLeftIcon />
            </GoBack>
            <h1 className={styles.heading}>Manage Appointment</h1>
            <p className={styles.description}>
              An overview of patient details and reports
            </p>
          </div>
          {signedIN ? (
            <div className="flex items-center gap-3">
              <button
                className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2 rounded-lg border border-[--primary-blue] md:border-none bg-transparent md:bg-[--pure-white] text-xs md:text-sm font-bold text-[--primary-blue]"
                onClick={() => setShowFilters(true)}
              >
                <FilterIcon />
                <span className="hidden md:inline">FILTER</span>
              </button>
              <Link
                to="/manage-appointment/schedule-appointment"
                className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2 gap-2 rounded-lg bg-[--primary-blue] text-[--pure-white] text-xs md:text-sm"
              >
                <PlusIcon stroke="#FFFFFF" />
                <span className="hidden md:inline">SCHEDULE APPOINTMENT</span>
              </Link>
            </div>
          ) : null}
        </div>

        {!authCheckComplete ? (
          <FullpageLoadingState />
        ) : signedIN ? (
          <>
            <div className="flex justify-center md:hidden">
              <div className="flex justify-between max-w-96 w-full gap-2 p-2 rounded-3xl bg-[--primary-blue-light]">
                <div
                  className={`font-bold text-xs py-1 px-2 text-[--primary-blue] tracking-widest rounded-3xl ${showAppointmentInfo ? "bg-[--pure-white]" : null}`}
                  onClick={() => {
                    setShowAppointmentInfo(true)
                  }}
                >
                  APPOINTMENT INFO
                </div>
                <div
                  className={`font-bold text-xs py-1 px-2 text-[--text-light-gray] tracking-widest rounded-3xl ${showAppointmentInfo ? null : "bg-[--pure-white]"}`}
                  onClick={() => {
                    setShowAppointmentInfo(false)
                  }}
                >
                  ALL APPOINTMENT
                </div>
              </div>
            </div>

            <div className="flex grow max-h-full overflow-scroll no-scrollbar md:overflow-hidden gap-6 h-min justify-center md:justify-normal">
              <div
                className={`overflow-hidden ${showAppointmentInfo ? "hidden" : "block"} md:block`}
              >
                <AppointmentDetailsTable />
              </div>
              <div
                className={`flex flex-col gap-4 ${showAppointmentInfo ? "flex" : "hidden"} md:flex grow max-w-96`}
              >
                <AppointmentCalendar
                  rootClassNameOnCollapsed="md:min-h-[14.8rem]"
                  onChange={handleCalendarDateChange}
                  disablePastDates={false}
                />

                <TimeAppointments />
              </div>
            </div>
          </>
        ) : (
          <ConnectGoogleAccComponent setSignedIn={setSignedIn} />
        )}

        {isAccessTokenValidLoading ||
        isGetCalendarBookingLoading ||
        isGetBookingLoading ? (
          <FullpageLoadingState />
        ) : null}

        {showFilters && (
          <>
            {/* Remove the background opacity from the overlay */}
            <div
              className="fixed inset-0 z-[50] bg-transparent"
              onClick={() => setShowFilters(false)}
            />
            <FilterContainer
              setShowFilters={setShowFilters}
              filterToBeExpanded={filterToBeExpanded}
              setFilterToBeExpanded={setFilterToBeExpanded}
              setPaginationState={setPaginationState}
              paginationState={paginationState}
              filters={filters}
              title="Filters"
              valueTransformFunctions={valueTransformFunctions}
            />
          </>
        )}
      </PageWrapper>
    </NavigationsPage>
  )
}

export default ManageAppointmentPage
